import type { RightBarTab } from "@v1/reducers/ui-reducer";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { AnalyticsMenuPane } from "@v1/components/analytics/AnalyticsMenuPane";
import InlineAlert from "@v1/components/designSystem/alert/InlineAlert";
import { toast } from "@v1/components/designSystem/alert/Toast";
import { DeprecatedTabs } from "@v1/components/designSystem/tabs/Tabs";
import DraftComponentPaneHeader from "@v1/components/DraftComponentPaneHeader";
import { RIGHT_BAR_WIDTH } from "@v1/components/editor/constants";
import { configMenuMap } from "@v1/components/editor/page/element-editor/components/config-menus/configMap";
import DesignTab from "@v1/components/editor/page/element-editor/components/DesignTab";
import InteractionsTab from "@v1/components/editor/page/element-editor/components/InteractionsTab";
import CustomPropModifier from "@v1/components/editor/page/element-editor/components/modifiers/CustomPropModifier";
import VariantModifier from "@v1/components/editor/page/element-editor/components/modifiers/VariantModifier";
import {
  RightBarBody,
  RightBarLayout,
} from "@v1/components/layouts/RightBarLayout";
import { useEditorPerformanceContext } from "@v1/contexts/editor-performance.context";
import useRightBarVisibility from "@v1/hooks/useRightBarVisibility";
import {
  selectDraftComponentHasVariants,
  selectDraftComponentRightBarTabs,
  selectDraftComponentType,
  selectDraftComponentTypeIsModal,
  selectEditorMode,
  selectNearestAncestorHasVariants,
  selectUpdatesSinceLastRequestFinished,
} from "@v1/reducers/core-reducer";
import {
  selectIsRightBarAnalyticsOpen,
  selectRightBarActiveTab,
  setRightBarActiveTab,
} from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { analytics, trackError } from "@v2/features/infra/analytics/tracking";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { isDevelopment } from "@v2/utils/env";
import {
  ErrorBoundary,
  ErrorBoundaryDefaultFallback,
} from "replo-runtime/shared/ErrorBoundary";

import RightBarHeader from "./RightBarHeader";

const ANALYTICS_RIGHT_BAR_WIDTH = 300;

const RightBar = () => {
  return (
    <>
      <RightBarHeader />
      <RightBarPanel />
    </>
  );
};

const RightBarPanel = React.memo(function RightBar() {
  const activeTab = useEditorSelector(selectRightBarActiveTab);
  const editorMode = useEditorSelector(selectEditorMode);

  const isRightBarVisible = useRightBarVisibility();
  const isRightBarAnalyticsOpen = useEditorSelector(
    selectIsRightBarAnalyticsOpen,
  );
  const { rightBarElementRef } = useEditorPerformanceContext();
  const draftComponentType = useEditorSelector(selectDraftComponentType);

  const hasVariants = useEditorSelector(selectDraftComponentHasVariants);
  const hasParentVariants = useEditorSelector(selectNearestAncestorHasVariants);
  const shouldShowVariantModifier =
    activeTab === "design" || hasVariants || hasParentVariants;

  const ConfigMenu =
    draftComponentType && configMenuMap[draftComponentType]?.config
      ? configMenuMap[draftComponentType].config
      : CustomPropModifier;

  const AccessibilityMenu =
    (draftComponentType && configMenuMap[draftComponentType]?.accessibility) ||
    null;
  const tabOptions = useEditorSelector(selectDraftComponentRightBarTabs);
  const dispatch = useEditorDispatch();
  useResetActiveTab();
  const logEvent = useLogAnalytics();
  const handleRenderError = React.useCallback(
    (error: unknown, info: React.ErrorInfo) => {
      if (isDevelopment) {
        console.error("[REPLO] Component rendering error in RightBar", {
          error,
          reactErrorInfo: info,
        });
      }
      toast({
        type: "error",
        header: "Something went wrong",
        message:
          "Please try refreshing the page or reach out to support if the error persists.",
        cta: "Contact Support",
        ctaHref: "mailto:support@replo.app",
      });
      trackError(error);
    },
    [],
  );

  if (!isRightBarVisible) {
    return null;
  }

  const designTabRightBarWidth = RIGHT_BAR_WIDTH;

  const isEditMode = editorMode === "edit";
  const rightBarWidth = isRightBarAnalyticsOpen
    ? ANALYTICS_RIGHT_BAR_WIDTH
    : designTabRightBarWidth;

  if (isRightBarAnalyticsOpen) {
    return (
      <AnalyticsMenuPane
        width={rightBarWidth}
        elementRef={rightBarElementRef}
      />
    );
  }

  const tabOptionsFiltered = tabOptions?.filter((tab) => tab.isVisible) || [];

  // https://stackoverflow.com/questions/14962468/how-can-i-combine-flexbox-and-vertical-scroll-in-a-full-height-app
  return (
    <RightBarLayout
      rightBarWidth={rightBarWidth}
      rightBarElementRef={rightBarElementRef}
    >
      <DeprecatedTabs.Root
        value={activeTab ?? "design"}
        onValueChange={(value) => {
          logEvent("editor.rightbar.tab.select", { tab: value as RightBarTab });
          dispatch(setRightBarActiveTab(value as RightBarTab));
        }}
      >
        {isEditMode && (
          // NOTE (Sebas, 2024-10-24): This is now on the RightBar and not in the
          // DraftComponentPane because it was interfering with the scrollable
          // component and causing weird horizontal and vertical scroll behavior.
          <div className="sticky top-0 z-10 flex flex-col gap-1 rounded-xl bg-white p-3 pb-0">
            <DraftComponentPaneHeader />
            <DeprecatedTabs.List>
              {tabOptionsFiltered?.map((option) => (
                <DeprecatedTabs.Trigger
                  key={option.value}
                  value={option.value}
                  size="sm"
                >
                  {option.label}
                </DeprecatedTabs.Trigger>
              ))}
            </DeprecatedTabs.List>
          </div>
        )}
        <RightBarBody isEditMode={isEditMode}>
          {/* Note (Noah, 2022-08-16, REPL-3607): This banner is here because we're
            suspicious of a bug where we think that user updates which are made under
            slow or volatile network conditions may sometimes result in lost work. This
            could possibly happen if the element update request was VERY slow to respond,
            and a lot of updates to the local element don't actually get sent to the
            backend because there's already a request in progress, then the user refreshes
            the page or something and loses the updates (since they've never been sent to
            the backend). This might not happen, but we're putting this banner here if they've
            made some arbitrary number of updates which would currently be lost if the page
            were to refresh, so that we can see it in full stories and debug. */}
          <UpdatesWarningBanner />
          <ErrorBoundary
            fallback={
              <div className="p-2 text-xs">
                <ErrorBoundaryDefaultFallback />
              </div>
            }
            onError={handleRenderError}
          >
            {draftComponentType && (
              <div
                className={twMerge(
                  "text-default flex max-w-[250px] flex-col gap-2 p-3 pb-20",
                  !isEditMode && "cursor-not-allowed",
                )}
              >
                {shouldShowVariantModifier && <VariantModifier />}
                <div className={twMerge(!isEditMode && "pointer-events-none")}>
                  <DeprecatedTabs.Content value="design">
                    <DesignTab />
                  </DeprecatedTabs.Content>
                  <DeprecatedTabs.Content value="interactions">
                    <InteractionsTab />
                  </DeprecatedTabs.Content>
                  <DeprecatedTabs.Content value="custom">
                    <ConfigMenu />
                  </DeprecatedTabs.Content>
                  {AccessibilityMenu && (
                    <DeprecatedTabs.Content value="accessibility">
                      <AccessibilityMenu />
                    </DeprecatedTabs.Content>
                  )}
                </div>
              </div>
            )}
          </ErrorBoundary>
        </RightBarBody>
      </DeprecatedTabs.Root>
    </RightBarLayout>
  );
});

const UpdatesWarningBanner = () => {
  const updatesSinceLastRequestFinished = useEditorSelector(
    selectUpdatesSinceLastRequestFinished,
  );

  const showBanner = updatesSinceLastRequestFinished >= 30;

  React.useEffect(() => {
    if (showBanner) {
      analytics.logEvent("editor.pendingUpdatesWarningBannerShown", {});
    }
  }, [showBanner]);

  if (!showBanner) {
    return null;
  }

  return (
    <div className="p-2">
      <InlineAlert variant="error" multiline>
        More than {updatesSinceLastRequestFinished} pending unsaved updates.
        This may be a bug - please reach out to support@replo.app
      </InlineAlert>
    </div>
  );
};

/**
 * Hook to reset active tab when the draft component changes
 */
function useResetActiveTab() {
  const isDraftComponentModal = useEditorSelector(
    selectDraftComponentTypeIsModal,
  );
  const dispatch = useEditorDispatch();
  const tabOptions = useEditorSelector(selectDraftComponentRightBarTabs);
  const activeTab = useEditorSelector(selectRightBarActiveTab);
  const isCurrentTabVisible = tabOptions?.find(
    (tab) => tab.value === activeTab,
  )?.isVisible;

  // NOTE (Sebas, 2025-01-14): When selecting multiple components we need to
  // check if the current tab is visible, if not, reset the tab to design to
  // avoid showing the wrong tab.
  React.useEffect(() => {
    if (!isCurrentTabVisible) {
      dispatch(
        setRightBarActiveTab(isDraftComponentModal ? "custom" : "design"),
      );
    }
  }, [dispatch, isCurrentTabVisible, isDraftComponentModal]);
}

export default RightBar;
